import { z } from "zod";
import {
  JobState,
  JobType,
  PhotoSource,
  CancellationReason,
  EditReason,
  LoadDecision,
} from "./enums";
import {
  Customer,
  Property,
  Truck,
  DumpsterSize,
  Landfill,
} from "./reference";
import { Driver } from "./driver";

/**
 * Job wire schemas — the core API contract.
 *
 * `Job` is the full record (detail view). The nested relations are optional
 * because list views omit them; detail views populate them.
 */

export const Job = z.object({
  id: z.string().uuid(),
  jobNumber: z.string(), // ED-2026-0001
  customerId: z.string().uuid(),
  propertyId: z.string().uuid(),
  customerSnapshot: z.record(z.unknown()), // frozen JSONB
  driverId: z.string().uuid().nullable(), // cleared on reassign
  // Unit #19: nullable. Office creates UNASSIGNED jobs (no driver,
  // no truck); the dispatcher assigns via /jobs/:id/assign. Mirrors
  // the 0007 ALTER TABLE on jobs.truck_id.
  truckId: z.string().uuid().nullable(),
  dumpsterSizeId: z.string().uuid(),
  jobType: JobType,
  // DO-LANDFILL-OPTIONAL: nullable — D/O (delivery-only) jobs are created
  // with landfill_id NULL (migration 0027). Mirrors the column; the nested
  // landfill relation below stays .optional() and buildJobDetail omits it
  // when landfill_id is NULL (same omission path as a hard-deleted reference).
  landfillId: z.string().uuid().nullable(),
  state: JobState,
  message: z.string().nullable(), // dispatcher free-text
  startAt: z.string().datetime().nullable(),
  finishAt: z.string().datetime().nullable(),
  durationSeconds: z.number().int().nullable(),
  // Unit #25: pause/resume tracking
  pausedAt: z.string().datetime().nullable(),
  pausedSeconds: z.number().int(),
  // Worker-served relative path (/api/v1/jobs/:id/photo), not an absolute URL
  // (SPEC-11 Option A, SPEC-18). Kept as a plain string for that reason.
  ticketPhotoUrl: z.string().nullable(),
  ticketPhotoAt: z.string().datetime().nullable(),
  photoSource: PhotoSource.nullable(),
  // Tenant setting denormalized on Job for driver flow (supervisor-approved pragmatic choice, #18 formalizes)
  erReturnMinutes: z.number().int().optional(),
  weightTons: z.number().nullable(), // office-entered
  disposalAmountCents: z.number().int().nullable(),
  billableAmountCents: z.number().int().nullable(),
  // Unit #26: office load-routing decision (SPEC-26). loadDecision is one of
  // LANDFILL | FACILITY_METAL or null; loadNote is free text or null. Mirrors
  // jobs.load_decision/load_note (migration 0014).
  loadDecision: LoadDecision.nullable(),
  loadNote: z.string().nullable(),
  cancellationReason: CancellationReason.nullable(),
  cancellationNote: z.string().nullable(),
  cancelledByUserId: z.string().uuid().nullable(),
  queuedOffline: z.boolean(),
  createdByUserId: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Nested relations (detail view only)
  customer: Customer.optional(),
  property: Property.optional(),
  driver: Driver.optional(),
  truck: Truck.optional(),
  dumpsterSize: DumpsterSize.optional(),
  landfill: Landfill.optional(),
});
export type Job = z.infer<typeof Job>;

/**
 * Input to POST /jobs.
 *
 * Unit #19: office creates UNASSIGNED jobs. `driverId` and `truckId`
 * are now optional at the schema level — the schema stays permissive
 * (shape-check only) and the route enforces the role-based rules:
 *
 *  - DISPATCHER: must provide BOTH `driverId` and `truckId` (the
 *    original flow, unchanged). A disabled dispatcher hits 403 BEFORE
 *    schema validation (cheap gate first, per #19 brief).
 *  - OFFICE: must NOT provide `driverId` or `truckId` (presence → 400
 *    "Office creates unassigned jobs"). The job lands as NEW with
 *    `driver_id = NULL, truck_id = NULL`; the dispatcher assigns later
 *    via the existing /jobs/:id/assign endpoint.
 *
 * The split is intentional: keeping the schema permissive avoids a
 * branched `OfficeNewJob` type and lets the same wire payload decode
 * shape-valid for either role; the role-specific rules live in the
 * route, where they belong.
 */
export const NewJob = z
  .object({
    customerId: z.string().uuid(),
    propertyId: z.string().uuid(),
    driverId: z.string().uuid().optional(),
    truckId: z.string().uuid().optional(),
    dumpsterSizeId: z.string().uuid(),
    jobType: JobType,
    // DO-LANDFILL-OPTIONAL: landfill is type-conditional. D/O (delivery-only)
    // sends NO landfill — the value is meaningless (nothing is dumped) — and
    // a SENT value is treated as a client bug (strict reject, not silent
    // ignore). S/O, E/R and R/O keep requiring it. See superRefine below.
    landfillId: z.string().uuid().optional(),
    message: z.string().optional(),
  })
  // The conditional rule lives here at the contract layer (not only in the
  // route) so every consumer of NewJob — now and future — inherits it. For a
  // malformed-but-present value (e.g. "abc" with D/O), the field-level
  // .uuid() check runs BEFORE this refine reports; the strict reject is the
  // runtime contract, the shape check is the wire contract.
  .superRefine((d, ctx) => {
    if (d.jobType === "D_O") {
      if (d.landfillId !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["landfillId"],
          message: "D/O jobs are delivery-only — no landfill",
        });
      }
    } else if (d.landfillId === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["landfillId"],
        message: "landfillId is required for S/O, E/R and R/O jobs",
      });
    }
  });
export type NewJob = z.infer<typeof NewJob>;

// Input to POST /jobs/:id/finish. SPEC-10 defines no body; the optional
// `queuedOffline` flag is accepted (strict, default false) for offline-queue
// replays — Unit #13 formalizes the full offline flow. An absent body or an
// empty JSON object is valid.
export const FinishJob = z
  .object({ queuedOffline: z.boolean().optional() })
  .strict();
export type FinishJob = z.infer<typeof FinishJob>;

// Input to POST /jobs/:id/cancel
export const CancelJob = z
  .object({
    reason: CancellationReason,
    note: z.string().optional(), // required when reason = OTHER_WITH_NOTE
  })
  .refine((d) => d.reason !== "OTHER_WITH_NOTE" || !!d.note?.trim(), {
    message: "note is required when reason is OTHER_WITH_NOTE",
    path: ["note"],
  });
export type CancelJob = z.infer<typeof CancelJob>;

// Input to POST /jobs/:id/review
export const ReviewJob = z
  .object({
    weightTons: z.number().positive(),
    disposalAmountCents: z.number().int().positive(),
    billableAmountCents: z.number().int().positive(),
  })
  .refine((d) => d.billableAmountCents >= d.disposalAmountCents, {
    message: "Billable amount must be ≥ disposal amount",
  });
export type ReviewJob = z.infer<typeof ReviewJob>;

// Input to POST /jobs/:id/review for D_O (delivery-only) jobs (PM-locked
// decision — money is meaningless when there's no weight ticket). Delivery-only
// jobs review with an EMPTY body and leave the money fields NULL. `.strict()`
// rejects any surprise money fields (a D/O review with money is a 400).
export const DoReviewJob = z.object({}).strict();
export type DoReviewJob = z.infer<typeof DoReviewJob>;

/**
 * GET /jobs/:id/audit — one audit-log entry for the job's review/edit trail.
 * `before` is NULL for the initial REVIEW; `after` carries the provided
 * fields only. `reason` is NULL for REVIEW (only edits carry reasons).
 */
export const AuditLogEntry = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  action: z.enum(["REVIEW", "UPDATE"]),
  reason: EditReason.nullable(),
  before: z.record(z.unknown()).nullable(),
  after: z.record(z.unknown()).nullable(),
  at: z.string().datetime(),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntry>;

// Input to PATCH /jobs/:id (edit REVIEWED job)
export const EditJob = z
  .object({
    weightTons: z.number().positive().optional(),
    disposalAmountCents: z.number().int().nonnegative().optional(),
    billableAmountCents: z.number().int().nonnegative().optional(),
    // Unit #26: office load-routing decision (SPEC-26). Both are OPTIONAL so
    // a money-only edit doesn't require them, but when supplied they replace
    // the whole decision: `loadDecision` accepts null (clear), LANDFILL, or
    // FACILITY_METAL; `loadNote` is bounded free text or null. A note without
    // a decision is allowed (SPEC's lightweight annotation contract — the
    // office may annotate before routing).
    loadDecision: LoadDecision.nullable().optional(),
    loadNote: z.string().max(2000).nullable().optional(),
    reason: EditReason, // REQUIRED
    note: z.string().optional(),
  })
  .refine(
    (d) => {
      if (
        d.billableAmountCents !== undefined &&
        d.disposalAmountCents !== undefined
      ) {
        return d.billableAmountCents >= d.disposalAmountCents;
      }
      return true;
    },
    { message: "Billable amount must be ≥ disposal amount" },
  );
export type EditJob = z.infer<typeof EditJob>;

// Input to POST /jobs/:id/reassign — no body (SPEC-14). Empty object keeps the
// schema contract explicit for the route.
export const ReassignJob = z.object({}).strict();
export type ReassignJob = z.infer<typeof ReassignJob>;

// Unit #25: Input to POST /jobs/:id/pause — optional note (e.g. "redirecting to ED-0042").
export const PauseJob = z
  .object({ note: z.string().max(500).optional() })
  .strict();
export type PauseJob = z.infer<typeof PauseJob>;

// Unit #25: Input to POST /jobs/:id/resume — no body.
export const ResumeJob = z.object({}).strict();
export type ResumeJob = z.infer<typeof ResumeJob>;

/**
 * Input to POST /jobs/:id/assign (Unit #14 Part 4) — assigns a driver to a
 * driverless NEW job.
 *
 * `truckId` is OPTIONAL at the schema level, but the route enforces a
 * CONDITIONAL rule (Unit #19 send-back Repair 1):
 *  - If the job already has a truck (`truck_id IS NOT NULL`), `truckId` is
 *    optional. Omitting it keeps the existing truck; supplying it switches
 *    the truck.
 *  - If the job has no truck (`truck_id IS NULL`, the office-created
 *    contract), `truckId` is REQUIRED. Omitting it yields
 *    `400 { error: "validation", details: { fieldErrors: { truckId: [
 *    "Truck is required — this job has no truck yet"] } } }`. This closes
 *    the door on a driver seeing a truckless job.
 *
 * The endpoint refuses to assign over an already-assigned job — that path is
 * `reassign` (clears the driver) then `assign` (sets the new one).
 */
export const AssignJob = z
  .object({
    driverId: z.string().uuid(),
    truckId: z.string().uuid().optional(),
  })
  .strict();
export type AssignJob = z.infer<typeof AssignJob>;
